'use client';
import { flip, FloatingPortal, offset, useFloating, useTransitionStyles } from '@floating-ui/react';
import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { parseDate, parseDateTime, toCalendarDateTime } from '@internationalized/date';
import { cx } from 'class-variance-authority';
import moment from 'moment';
import React, { useState } from 'react';
import { AriaDatePickerProps } from 'react-aria';
import { DatePicker as AriaDatePicker, Label as AriaLabel, DateValue } from 'react-aria-components';

import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { buttonClassNames, StyledInputProps, TooltipProps } from '~/components/ui';
import { containerClassNames, labelClassNames } from '~/components/ui/Form/StyledInput';
import { cn } from '~/lib/cn';
import {
    Calendar,
    CalendarCell,
    CalendarGrid,
    CalendarGridBody,
    CalendarGridHeader,
    CalendarHeaderCell,
    CalendarHeading
} from './Calendar';
import { DateInput } from './DateField';

type PartialAriaDatePickerProps = Pick<
    AriaDatePickerProps<DateValue>,
    'isDisabled' | 'onBlur' | 'isInvalid' | 'granularity' | 'shouldForceLeadingZeros' | 'placeholderValue'
>;

interface Props
    extends Partial<Omit<StyledInputProps, keyof AriaDatePickerProps<DateValue>>>, PartialAriaDatePickerProps {
    name?: string;
    label?: string;
    onChange?: (date: any) => void;
    value?: DateValue | null | Date | string;
    inline?: boolean;
    placement?: TooltipProps['placement'];
    withoutShifting?: boolean;
    containerClassName?: string;
    /** @deprecated */
    utcMode?: boolean;
    /** @deprecated */
    borderless?: boolean;
    /** @deprecated */
    pickerProps?: any;
    /** @deprecated */
    popperPlacement?: any;
    /** @deprecated */
    disableMessages?: true;
    ref?: React.RefCallback<HTMLDivElement>;
}

const DatePicker = ({
    name,
    label,
    placement,
    flush,
    onChange,
    disabled: _disabled,
    withoutShifting,
    inline,
    helperText,
    containerClassName,
    granularity,
    shouldForceLeadingZeros = true,
    placeholderValue,
    isDisabled,
    value: _value,
    isInvalid,
    ref,
    onBlur,
    ...inputProps
}: Props) => {
    const [isOpen, setIsOpen] = useState(false);
    const disabled = isDisabled ?? _disabled;
    const { refs, floatingStyles, context } = useFloating({
        strategy: 'fixed',
        placement,
        open: isOpen,
        onOpenChange: setIsOpen,
        middleware: [offset(12), flip()]
    });
    const { isMounted, styles } = useTransitionStyles(context, { duration: 100 });

    // whether the picker should include a time part (e.g. granularity="minute")
    const isDateTime = !!granularity && granularity !== 'day';

    // parses the raw form value (ISO string/Date) into a react-aria date(-time) value
    const parseValue = (rawValue: any) => {
        if (!rawValue) {
            return null;
        }

        try {
            return isDateTime
                ? parseDateTime(moment(rawValue).format('YYYY-MM-DDTHH:mm:ss'))
                : parseDate(moment(rawValue).format('YYYY-MM-DD'));
        } catch {
            return null;
        }
    };

    const value = !_value ? null : parseValue(_value);
    const resolvedPlaceholderValue =
        placeholderValue ??
        (isDateTime ? parseDateTime(moment().format('YYYY-MM-DDTHH:mm:00')) : parseDate(moment().format('YYYY-MM-DD')));

    // emits the picked value as a naive ISO string, preserving the set time when the calendar returns a date-only value
    const handleChange = (calendarValue: DateValue | null) => {
        if (!calendarValue) {
            return onChange?.(null);
        }

        let next = calendarValue;
        if (isDateTime && !('hour' in next)) {
            next = toCalendarDateTime(next);
            if (value && 'hour' in value) {
                next = next.set({ hour: value.hour, minute: value.minute });
            }
        }

        onChange?.(next.toString());
    };

    if (inline) {
        return (
            <AriaDatePicker
                granularity={granularity}
                isOpen={isOpen}
                onOpenChange={setIsOpen}
                onChange={handleChange}
                value={value}
                name={name}
                shouldForceLeadingZeros={shouldForceLeadingZeros}
                placeholderValue={resolvedPlaceholderValue}
                isDisabled={disabled}
                ref={ref}
                onBlur={onBlur}
            >
                <Calendar onChange={handleChange} value={value}>
                    <CalendarHeading />
                    <CalendarGrid>
                        <CalendarGridHeader>
                            {(day) => <CalendarHeaderCell>{day}</CalendarHeaderCell>}
                        </CalendarGridHeader>
                        <CalendarGridBody>{(date) => <CalendarCell date={date} />}</CalendarGridBody>
                    </CalendarGrid>
                </Calendar>
            </AriaDatePicker>
        );
    }

    return (
        <AriaDatePicker
            granularity={granularity}
            isOpen={isOpen}
            onOpenChange={setIsOpen}
            onChange={handleChange}
            value={value}
            name={name}
            shouldForceLeadingZeros={shouldForceLeadingZeros}
            placeholderValue={resolvedPlaceholderValue}
            isDisabled={disabled}
            ref={(r) => {
                refs.setReference(r);
                ref?.(r);
            }}
            onBlur={onBlur}
            className={cn(containerClassNames({ flush: withoutShifting || flush }), containerClassName, 'tw-mt-3')}
        >
            {label && <AriaLabel className={labelClassNames({})}>{label}</AriaLabel>}
            <div className="tw-flex tw-items-stretch">
                <DateInput disabled={disabled} {...inputProps} />
                <button
                    disabled={disabled}
                    className={cn(
                        buttonClassNames({
                            color: 'light',
                            size: inputProps.size || 'sm',
                            disabled,
                            flat: true
                        }),
                        'tw-min-h-auto tw-h-auto tw-rounded-e-none tw-rounded-s-none tw-py-1 focus:tw-border-success'
                    )}
                    type="button"
                    onClick={() => {
                        onChange?.(null);
                        setIsOpen(false);
                    }}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
                <button
                    disabled={disabled}
                    className={cn(
                        buttonClassNames({
                            color: 'light',
                            size: inputProps.size || 'sm',
                            disabled,
                            flat: true
                        }),
                        'tw-min-h-auto tw-h-auto tw-rounded-s-none tw-py-1 focus:tw-border-success'
                    )}
                    type="button"
                    onClick={() => setIsOpen((state) => !state)}
                >
                    <FontAwesomeIcon icon={faCalendar} />
                </button>
            </div>
            {helperText && (
                <span
                    className={cx(
                        isInvalid ? 'tw-text-error' : 'tw-text-accents-6',
                        'tw-ml-2 tw-mt-1 tw-block tw-text-[10px]'
                    )}
                >
                    {helperText}
                </span>
            )}
            {isMounted && isOpen && (
                <FloatingPortal>
                    <div
                        className="tw-z-[99999] tw-rounded-xl tw-border tw-border-solid tw-border-accents-2 tw-bg-white tw-p-2 tw-shadow-lg"
                        style={{ ...floatingStyles, ...styles }}
                        // eslint-disable-next-line react-hooks/refs
                        ref={refs.setFloating}
                    >
                        <Calendar onChange={handleChange} value={value}>
                            <CalendarHeading />
                            <CalendarGrid>
                                <CalendarGridHeader>
                                    {(day) => <CalendarHeaderCell>{day}</CalendarHeaderCell>}
                                </CalendarGridHeader>
                                <CalendarGridBody>{(date) => <CalendarCell date={date} />}</CalendarGridBody>
                            </CalendarGrid>
                        </Calendar>
                    </div>
                </FloatingPortal>
            )}
        </AriaDatePicker>
    );
};

export default DatePicker;
export type { Props as DatePickerProps };
